import { tv } from 'tailwind-variants';
import { focusStyles } from '../../styles/focusStyles.esm.js';

// Define the button styles using the tv function
const buttonStyles = tv({
    // Base styles applied to all buttons
    slots: {
        base: `
      iris:inline-flex
      iris:cursor-pointer
      iris:items-center
      iris:justify-center
      iris:gap-(--comp-space-gap-button-icon)
      iris:rounded-(--comp-size-radius-button)
      iris:border
      iris:border-(length:--comp-size-border-button)
      iris:py-(--comp-space-padding-button-vertical)
      iris:font-(--comp-type-weight-button)
      iris:[text-transform:var(--comp-type-transform-button)]
      iris:no-underline
      iris:duration-(--comp-motion-duration-button)
      iris:ease-(--comp-motion-easing-button)
    `,
        hasIcon: `
      iris:flex
      iris:shrink-0
      iris:items-center
      iris:justify-center
    `,
        spinnerArc: "",
    },
    variants: {
        // Size variants for the button
        size: {
            sm: {
                base: `
          iris:h-(--comp-size-height-button-sm)
          iris:px-(--comp-space-padding-button-sm-horizontal)
          iris:text-(length:--comp-type-size-button-sm)
        `,
                hasIcon: "iris:size-(--comp-size-icon-button-sm)",
            },
            md: {
                base: `
          iris:h-(--comp-size-height-button-md)
          iris:px-(--comp-space-padding-button-md-horizontal)
          iris:text-(length:--comp-type-size-button-md)
        `,
                hasIcon: "iris:size-(--comp-size-icon-button-md)",
            },
            lg: {
                base: `
          iris:h-(--comp-size-height-button-lg)
          iris:px-(--comp-space-padding-button-lg-horizontal)
          iris:text-(length:--comp-type-size-button-lg)
        `,
                hasIcon: "iris:size-(--comp-size-icon-button-lg)",
            },
        },
        // Responsive size variants
        smSize: {
            sm: {
                base: `
          iris:sm:h-(--comp-size-height-button-sm)
          iris:sm:px-(--comp-space-padding-button-sm-horizontal)
          iris:sm:text-(length:--comp-type-size-button-sm)
        `,
                hasIcon: "iris:sm:size-(--comp-size-icon-button-sm)",
            },
            md: {
                base: `
          iris:sm:h-(--comp-size-height-button-md)
          iris:sm:px-(--comp-space-padding-button-md-horizontal)
          iris:sm:text-(length:--comp-type-size-button-md)
        `,
                hasIcon: "iris:sm:size-(--comp-size-icon-button-md)",
            },
            lg: {
                base: `
          iris:sm:h-(--comp-size-height-button-lg)
          iris:sm:px-(--comp-space-padding-button-lg-horizontal)
          iris:sm:text-(length:--comp-type-size-button-lg)
        `,
                hasIcon: "iris:sm:size-(--comp-size-icon-button-lg)",
            },
        },
        mdSize: {
            sm: {
                base: `
          iris:md:h-(--comp-size-height-button-sm)
          iris:md:px-(--comp-space-padding-button-sm-horizontal)
          iris:md:text-(length:--comp-type-size-button-sm)
        `,
                hasIcon: "iris:md:size-(--comp-size-icon-button-sm)",
            },
            md: {
                base: `
          iris:md:h-(--comp-size-height-button-md)
          iris:md:px-(--comp-space-padding-button-md-horizontal)
          iris:md:text-(length:--comp-type-size-button-md)
        `,
                hasIcon: "iris:md:size-(--comp-size-icon-button-md)",
            },
            lg: {
                base: `
          iris:md:h-(--comp-size-height-button-lg)
          iris:md:px-(--comp-space-padding-button-lg-horizontal)
          iris:md:text-(length:--comp-type-size-button-lg)
        `,
                hasIcon: "iris:md:size-(--comp-size-icon-button-lg)",
            },
        },
        lgSize: {
            sm: {
                base: `
          iris:lg:h-(--comp-size-height-button-sm)
          iris:lg:px-(--comp-space-padding-button-sm-horizontal)
          iris:lg:text-(length:--comp-type-size-button-sm)
        `,
                hasIcon: "iris:lg:size-(--comp-size-icon-button-sm)",
            },
            md: {
                base: `
          iris:lg:h-(--comp-size-height-button-md)
          iris:lg:px-(--comp-space-padding-button-md-horizontal)
          iris:lg:text-(length:--comp-type-size-button-md)
        `,
                hasIcon: "iris:lg:size-(--comp-size-icon-button-md)",
            },
            lg: {
                base: `
          iris:lg:h-(--comp-size-height-button-lg)
          iris:lg:px-(--comp-space-padding-button-lg-horizontal)
          iris:lg:text-(length:--comp-type-size-button-lg)
        `,
                hasIcon: "iris:lg:size-(--comp-size-icon-button-lg)",
            },
        },
        // Style variants for the button
        variant: {
            primary: {},
            secondary: {},
            tertiary: {},
            unstyled: {},
        },
        inverse: {
            true: {
                base: focusStyles.inverse,
            },
            false: {
                base: focusStyles.default,
            },
        },
        disabled: {
            true: { base: "iris:cursor-not-allowed" },
            false: {},
        },
        iconPosition: {
            left: {},
            right: {},
        },
        isLoading: {
            true: { base: "iris:cursor-not-allowed" },
            false: "",
        },
    },
    // Compound variants for the button
    compoundVariants: [
        {
            variant: "unstyled",
            disabled: false,
            inverse: false,
            className: {
                base: `
          iris:h-auto
          iris:border-none
          iris:bg-transparent
          iris:p-0
        `,
            },
        },
        {
            variant: "primary",
            disabled: false,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-rest)
          iris:bg-(--comp-color-background-button-primary-rest)
          iris:text-(--comp-color-content-button-primary-rest)
          iris:hover:border-(--comp-color-border-button-primary-hover)
          iris:hover:bg-(--comp-color-background-button-primary-hover)
          iris:hover:text-(--comp-color-content-button-primary-hover)
          iris:active:border-(--comp-color-border-button-primary-pressed)
          iris:active:bg-(--comp-color-background-button-primary-pressed)
          iris:active:text-(--comp-color-content-button-primary-pressed)
        `,
            },
        },
        {
            variant: "primary",
            disabled: false,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-inverse-rest)
          iris:bg-(--comp-color-background-button-primary-inverse-rest)
          iris:text-(--comp-color-content-button-primary-inverse-rest)
          iris:hover:border-(--comp-color-border-button-primary-inverse-hover)
          iris:hover:bg-(--comp-color-background-button-primary-inverse-hover)
          iris:hover:text-(--comp-color-content-button-primary-inverse-hover)
          iris:active:border-(--comp-color-border-button-primary-inverse-pressed)
          iris:active:bg-(--comp-color-background-button-primary-inverse-pressed)
          iris:active:text-(--comp-color-content-button-primary-inverse-pressed)
        `,
            },
        },
        {
            variant: "secondary",
            disabled: false,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-rest)
          iris:bg-(--comp-color-background-button-secondary-rest)
          iris:text-(--comp-color-content-button-secondary-rest)
          iris:hover:border-(--comp-color-border-button-secondary-hover)
          iris:hover:bg-(--comp-color-background-button-secondary-hover)
          iris:hover:text-(--comp-color-content-button-secondary-hover)
          iris:active:border-(--comp-color-border-button-secondary-pressed)
          iris:active:bg-(--comp-color-background-button-secondary-pressed)
          iris:active:text-(--comp-color-content-button-secondary-pressed)
        `,
            },
        },
        {
            variant: "secondary",
            disabled: false,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-inverse-rest)
          iris:bg-(--comp-color-background-button-secondary-inverse-rest)
          iris:text-(--comp-color-content-button-secondary-inverse-rest)
          iris:hover:border-(--comp-color-border-button-secondary-inverse-hover)
          iris:hover:bg-(--comp-color-background-button-secondary-inverse-hover)
          iris:hover:text-(--comp-color-content-button-secondary-inverse-hover)
          iris:active:border-(--comp-color-border-button-secondary-inverse-pressed)
          iris:active:bg-(--comp-color-background-button-secondary-inverse-pressed)
          iris:active:text-(--comp-color-content-button-secondary-inverse-pressed)
        `,
            },
        },
        {
            variant: "tertiary",
            disabled: false,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-rest)
          iris:bg-(--comp-color-background-button-tertiary-rest)
          iris:text-(--comp-color-content-button-tertiary-rest)
          iris:hover:border-(--comp-color-border-button-tertiary-hover)
          iris:hover:bg-(--comp-color-background-button-tertiary-hover)
          iris:hover:text-(--comp-color-content-button-tertiary-hover)
          iris:active:border-(--comp-color-border-button-tertiary-pressed)
          iris:active:bg-(--comp-color-background-button-tertiary-pressed)
          iris:active:text-(--comp-color-content-button-tertiary-pressed)
        `,
            },
        },
        {
            variant: "tertiary",
            disabled: false,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-inverse-rest)
          iris:bg-(--comp-color-background-button-tertiary-inverse-rest)
          iris:text-(--comp-color-content-button-tertiary-inverse-rest)
          iris:hover:border-(--comp-color-border-button-tertiary-inverse-hover)
          iris:hover:bg-(--comp-color-background-button-tertiary-inverse-hover)
          iris:hover:text-(--comp-color-content-button-tertiary-inverse-hover)
          iris:active:border-(--comp-color-border-button-tertiary-inverse-pressed)
          iris:active:bg-(--comp-color-background-button-tertiary-inverse-pressed)
          iris:active:text-(--comp-color-content-button-tertiary-inverse-pressed)
        `,
            },
        },
        {
            variant: "primary",
            disabled: true,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-disabled)
          iris:bg-(--comp-color-background-button-primary-disabled)
          iris:text-(--comp-color-content-button-primary-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "primary",
            disabled: true,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-inverse-disabled)
          iris:bg-(--comp-color-background-button-primary-inverse-disabled)
          iris:text-(--comp-color-content-button-primary-inverse-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "secondary",
            disabled: true,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-disabled)
          iris:bg-(--comp-color-background-button-secondary-disabled)
          iris:text-(--comp-color-content-button-secondary-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "secondary",
            disabled: true,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-inverse-disabled)
          iris:bg-(--comp-color-background-button-secondary-inverse-disabled)
          iris:text-(--comp-color-content-button-secondary-inverse-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "tertiary",
            disabled: true,
            inverse: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-disabled)
          iris:bg-(--comp-color-background-button-tertiary-disabled)
          iris:text-(--comp-color-content-button-tertiary-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        {
            variant: "tertiary",
            disabled: true,
            inverse: true,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-inverse-disabled)
          iris:bg-(--comp-color-background-button-tertiary-inverse-disabled)
          iris:text-(--comp-color-content-button-tertiary-inverse-disabled)
          iris:opacity-(--sem-opacity-interactive-disabled)
        `,
            },
        },
        // Spinner states
        {
            variant: "primary",
            inverse: false,
            className: {
                spinnerArc: "iris:stroke-(--comp-color-content-button-primary-disabled)",
            },
        },
        {
            variant: "primary",
            inverse: true,
            className: {
                spinnerArc: "iris:stroke-(--comp-color-content-button-primary-inverse-disabled)",
            },
        },
        {
            variant: "secondary",
            inverse: false,
            className: {
                spinnerArc: "iris:stroke-(--comp-color-content-button-secondary-disabled)",
            },
        },
        {
            variant: "secondary",
            inverse: true,
            className: {
                spinnerArc: "iris:stroke-(--comp-color-content-button-secondary-inverse-disabled)",
            },
        },
        {
            variant: "tertiary",
            inverse: false,
            className: {
                spinnerArc: "iris:stroke-(--comp-color-content-button-tertiary-disabled)",
            },
        },
        {
            variant: "tertiary",
            inverse: true,
            className: {
                spinnerArc: "iris:stroke-(--comp-color-content-button-tertiary-inverse-disabled)",
            },
        },
        // Loading states - using disabled tokens
        {
            variant: "primary",
            isLoading: true,
            inverse: false,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-disabled)
          iris:bg-(--comp-color-background-button-primary-disabled)
          iris:hover:border-(--comp-color-border-button-primary-disabled)
          iris:hover:bg-(--comp-color-background-button-primary-disabled)
          iris:active:border-(--comp-color-border-button-primary-disabled)
          iris:active:bg-(--comp-color-background-button-primary-disabled)
        `,
            },
        },
        {
            variant: "primary",
            isLoading: true,
            inverse: true,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-primary-inverse-disabled)
          iris:bg-(--comp-color-background-button-primary-inverse-disabled)
          iris:hover:border-(--comp-color-border-button-primary-inverse-disabled)
          iris:hover:bg-(--comp-color-background-button-primary-inverse-disabled)
          iris:active:border-(--comp-color-border-button-primary-inverse-disabled)
          iris:active:bg-(--comp-color-background-button-primary-inverse-disabled)
        `,
            },
        },
        {
            variant: "secondary",
            isLoading: true,
            inverse: false,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-disabled)
          iris:bg-(--comp-color-background-button-secondary-disabled)
          iris:hover:border-(--comp-color-border-button-secondary-disabled)
          iris:hover:bg-(--comp-color-background-button-secondary-disabled)
          iris:active:border-(--comp-color-border-button-secondary-disabled)
          iris:active:bg-(--comp-color-background-button-secondary-disabled)
        `,
            },
        },
        {
            variant: "secondary",
            isLoading: true,
            inverse: true,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-secondary-inverse-disabled)
          iris:bg-(--comp-color-background-button-secondary-inverse-disabled)
          iris:hover:border-(--comp-color-border-button-secondary-inverse-disabled)
          iris:hover:bg-(--comp-color-background-button-secondary-inverse-disabled)
          iris:active:border-(--comp-color-border-button-secondary-inverse-disabled)
          iris:active:bg-(--comp-color-background-button-secondary-inverse-disabled)
        `,
            },
        },
        {
            variant: "tertiary",
            isLoading: true,
            inverse: false,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-disabled)
          iris:bg-(--comp-color-background-button-tertiary-disabled)
          iris:hover:border-(--comp-color-border-button-tertiary-disabled)
          iris:hover:bg-(--comp-color-background-button-tertiary-disabled)
          iris:active:border-(--comp-color-border-button-tertiary-disabled)
          iris:active:bg-(--comp-color-background-button-tertiary-disabled)
        `,
            },
        },
        {
            variant: "tertiary",
            isLoading: true,
            inverse: true,
            disabled: false,
            className: {
                base: `
          iris:border-(--comp-color-border-button-tertiary-inverse-disabled)
          iris:bg-(--comp-color-background-button-tertiary-inverse-disabled)
          iris:hover:border-(--comp-color-border-button-tertiary-inverse-disabled)
          iris:hover:bg-(--comp-color-background-button-tertiary-inverse-disabled)
          iris:active:border-(--comp-color-border-button-tertiary-inverse-disabled)
          iris:active:bg-(--comp-color-background-button-tertiary-inverse-disabled)
        `,
            },
        },
    ],
    defaultVariants: {
        size: "md",
        variant: "primary",
        inverse: false,
        disabled: false,
        iconPosition: "left",
    },
});

export { buttonStyles as default };
